import {
  useContext,
  useMemo,
  useCallback,
  createContext,
  useState,
  ReactNode,
} from 'react';
import * as cnxId from '@cnx-saas/utils-cnx-id';
import { SnackbarProps } from './Snackbar.types';
import { Snackbar } from './Snackbar';

export type Alert = Omit<SnackbarProps, 'onClose'> & {
  onClose?: () => void;
};

interface SnackbarContext {
  addAlert: (alert: Alert) => void;
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const SnackbarContext = createContext<SnackbarContext>(undefined!);

export const useSnackbar = () => useContext(SnackbarContext);

interface SnackbarProviderProps {
  children: ReactNode;
}

export function SnackbarProvider({ children }: SnackbarProviderProps) {
  const [alerts, setAlerts] = useState<SnackbarProps[]>([]);

  const removeAlert = useCallback(
    () => setAlerts((prevAlerts) => prevAlerts.slice(0, prevAlerts.length - 1)),
    [],
  );

  const addAlert = useCallback(
    (alert: Alert) =>
      setAlerts((prevAlerts) => [
        {
          onClose: () => {
            alert.onClose?.();
            removeAlert();
          },
          ...alert,
        },
        ...prevAlerts,
      ]),
    [removeAlert],
  );

  const value = useMemo(() => ({ addAlert }), [addAlert]);

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      {alerts.length > 0 && (
        <Snackbar
          key={`${alerts[alerts.length - 1].type}-${cnxId.shortRandomId()}`}
          shouldShowCloseButton
          {...alerts[alerts.length - 1]}
        />
      )}
    </SnackbarContext.Provider>
  );
}
